import { Component, ErrorHandler, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, Injector, afterNextRender, computed, inject, signal } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { disabled, FieldTree, form, min, required } from '@angular/forms/signals'

import { MatInput } from '@angular/material/input'
import { MatSnackBar } from '@angular/material/snack-bar'

import { KayttajaService } from 'app/_angular/service/kayttaja.service'
import { AsetustenOletuksetService } from '../../_angular/service/asetusten-oletukset.service'
import { LaskuService } from '../../_angular/service/lasku/lasku.service'
import { LEMONAID_CF_API, LemonHttpService } from '../../_angular/service/lemon-http.service'
import { arvonMuutokset, asetaArvo } from '../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from '../../_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from '../../_jaettu-angular/service/form-validation.service'
import { LadataanService } from '../../_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from '../../_jaettu-angular/service/lemon-translation.service'
import { TiedostojenLataamisService } from '../../_jaettu-angular/service/tiedostojen-lataamis.service'
import { LaskuttavaAsiakasProvider } from '../../_jaettu-angular/service/lasku/laskuttava-asiakas.service'

import { Laskuasetukset, VerottomanMyynninPeruste } from '@lemon/core-firebase/model/lasku'
import { LaskuKopioija } from '@lemon/core-firebase/service/lasku/lasku.kopioija'
import { BicService } from '@lemon/core/service/bic.service'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { IbanService } from '@lemon/core/service/iban.service'
import { StringService } from '@lemon/core/service/string.service'
import { LemonaidValidators } from 'app/_angular/_validator/LemonaidValidators'

import { FileSystemFileEntry } from '../../_jaettu-angular/file-drop/file-drop-dom.types'
import { FileDropEntry } from '../../_jaettu-angular/file-drop/file-drop-entry'

import { Observable, Subject, combineLatest } from 'rxjs'
import { map, takeUntil } from 'rxjs/operators'
import { LaskutusprofiiliService } from '../../_angular/service/lasku/laskutusprofiili.service'

export type LaskuasetustenOsio = 'laskutusprofiilit' | 'ulkoasu' | 'oletukset'

const OSION_KENTAT: Record<LaskuasetustenOsio, (keyof LaskuasetuksetLomake)[]> = {
  laskutusprofiilit: ['pankki', 'iban', 'bic'],
  ulkoasu: ['ohjeet', 'piilotaYrityksenNimiLaskulla'],
  oletukset: ['oletusMaksuaikaa', 'verottomanMyynninOletusperuste', 'laskurivienHinnatVerollisina'],
}

const OLETUSMAKSUAIKA = 14

interface LaskuasetuksetLomake {
  ohjeet: string
  pankki: string
  iban: string
  bic: string
  piilotaYrityksenNimiLaskulla: boolean
  oletusMaksuaikaa: number | null
  verottomanMyynninOletusperuste: VerottomanMyynninPeruste | null
  laskurivienHinnatVerollisina: boolean
}

@Component({
  selector: 'app-laskuasetukset',
  templateUrl: './laskuasetukset.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LaskuasetuksetComponent implements OnInit, OnDestroy {
  @Input() osio: LaskuasetustenOsio = 'ulkoasu'
  @ViewChild(MatInput, { static: true }) nimiInput: MatInput
  @ViewChild('fileInput', { static: true }) fileInput

  private ngUnsubscribe: Subject<void> = new Subject<void>()
  private readonly _injector = inject(Injector)
  private readonly _piilotaYrityksenNimiPoissa = signal(false)
  private readonly _osionKentat = signal<(keyof LaskuasetuksetLomake)[]>([])
  private readonly _kieli = toSignal(inject(LemonTranslationService).currentLanguageObservable)

  lomake = form(
    signal<LaskuasetuksetLomake>({
      ohjeet: '',
      pankki: '',
      iban: '',
      bic: '',
      piilotaYrityksenNimiLaskulla: false,
      oletusMaksuaikaa: null,
      verottomanMyynninOletusperuste: null,
      laskurivienHinnatVerollisina: false,
    }),
    polku => {
      required(polku.pankki)
      required(polku.iban)
      validoiReaktiivisesti(polku.iban, LemonaidValidators.ibanValidator)
      required(polku.bic)
      validoiReaktiivisesti(polku.bic, LemonaidValidators.bicValidator)
      required(polku.oletusMaksuaikaa)
      min(polku.oletusMaksuaikaa, 1)
      disabled(polku.ohjeet, () => this._onkoMuunOsionKentta('ohjeet'))
      disabled(polku.pankki, () => this._onkoMuunOsionKentta('pankki'))
      disabled(polku.iban, () => this._onkoMuunOsionKentta('iban'))
      disabled(polku.bic, () => this._onkoMuunOsionKentta('bic'))
      disabled(polku.piilotaYrityksenNimiLaskulla, () => this._onkoMuunOsionKentta('piilotaYrityksenNimiLaskulla') || this._piilotaYrityksenNimiPoissa())
      disabled(polku.oletusMaksuaikaa, () => this._onkoMuunOsionKentta('oletusMaksuaikaa'))
      disabled(polku.verottomanMyynninOletusperuste, () => this._onkoMuunOsionKentta('verottomanMyynninOletusperuste'))
      disabled(polku.laskurivienHinnatVerollisina, () => this._onkoMuunOsionKentta('laskurivienHinnatVerollisina'))
    },
  )
  readonly fieldErrors = computed(() => {
    // Virheviestit käännetään nykyisellä kielellä, joten kielen vaihto muodostaa ne uudelleen
    this._kieli()
    return this.validationService.updateValidationStatus('laskuasetukset', this.lomake)
  })
  readonly logo = signal('/assets/noimage.png')
  readonly kuvavirheAvain = signal('')
  readonly kuvavirheParametrit = signal({})
  readonly optimoidaanKuvaa = signal(false)
  asetukset: Laskuasetukset = null
  readonly kuvanInfotekstinAvain = signal('laskuasetukset.logo.info-teksti')
  readonly maksuaikaVaihtoehdot = computed(() => {
    const vaihtoehdot = [7, 14, 21, 30, 45, 60]
    const nykyinen = this.lomake.oletusMaksuaikaa().value()
    if (nykyinen && !vaihtoehdot.includes(nykyinen)) {
      vaihtoehdot.push(nykyinen)
    }
    return vaihtoehdot.sort((a, b) => a - b)
  })
  verottomanMyynninPerusteet = Object.values(VerottomanMyynninPeruste)
  alvRekisterissaObservable: Observable<boolean>
  alvRekisterinTilaAvainObservable: Observable<string>

  constructor(
    public kayttajaService: KayttajaService,
    private laskuService: LaskuService,
    private bicService: BicService,
    private ibanService: IbanService,
    private validationService: FormValidationService,
    private laskuKopioija: LaskuKopioija,
    private asetuksetOletuksetService: AsetustenOletuksetService,
    private errorHandler: ErrorHandler,
    private ladataanService: LadataanService,
    private httpService: LemonHttpService,
    private snackbar: MatSnackBar,
    private currencyService: CurrencyService,
    private translationService: LemonTranslationService,
    private tiedostojenLataamisService: TiedostojenLataamisService,
    private stringService: StringService,
    private _laskuttavaAsiakasProvider: LaskuttavaAsiakasProvider,
    private _laskutusprofiiliService: LaskutusprofiiliService,
  ) {}

  public async kasitteleLogo(entries: FileDropEntry[]): Promise<void> {
    const tiedostonjenMaara = entries.length
    const fileEntrySuspect = tiedostonjenMaara > 0 ? entries[0] : null
    const fileSuspect = fileEntrySuspect && fileEntrySuspect.fileEntry && fileEntrySuspect.fileEntry.isFile ? (fileEntrySuspect.fileEntry as FileSystemFileEntry) : null
    const fileEnding = this.tiedostojenLataamisService.getFileEndingFromNgxFileDropEntry(fileEntrySuspect)
    const file: File = await this.tiedostojenLataamisService.getFile(fileSuspect)
    const fileSize = fileSuspect ? file.size : -1

    if (!this.tiedostoVoidaanLadata(fileSuspect ? tiedostonjenMaara : 0, fileEnding, fileSize)) {
      return
    }

    this.optimoiLogo(file, fileEntrySuspect.relativePath).catch(error => {
      this.optimoidaanKuvaa.set(false)
      this.kuvavirheAvain.set('laskuasetukset.logo.virheet.tuntematon')
      setTimeout(() => {
        this.kuvavirheAvain.set('')
      }, 10000)
      this.errorHandler.handleError(error)
    })
  }

  private optimoiLogo(file: File, relativePath: string): Promise<void> {
    this.optimoidaanKuvaa.set(true)
    const formData = new FormData()
    formData.append('logo', file, relativePath)
    return this.httpService
      .postBinaryGetBinary('/asetuksetOptimoiLaskulogo', formData, LEMONAID_CF_API)
      .then(kasiteltyKuva => {
        return this.tiedostojenLataamisService.getAsUint8Array(kasiteltyKuva)
      })
      .then(uint8Array => {
        const base64Img = this.tiedostojenLataamisService.uint8ArrayToBase64(uint8Array)
        this.asetukset.base64Logo = base64Img
        this.lomake().markAsDirty()
        this.updateLogoSrc()
        this.kuvanInfotekstinAvain.set('laskuasetukset.logo.muista-tallentaa')
        this.optimoidaanKuvaa.set(false)
      })
  }

  public fileOver(event) {
    // console.log(event)
  }

  public fileLeave(event) {
    // console.log(event)
  }

  public poistaKuva(event) {
    event.stopPropagation()
    this.asetukset.base64Logo = null
    this.lomake().markAsDirty()
    this.kuvanInfotekstinAvain.set('laskuasetukset.logo.muista-tallentaa-poisto')
    this.paivitaPiilotaYrityksenNimiLaskullaCheckBox()
    this.updateLogoSrc()
  }

  public naytaPoista(): boolean {
    return this.asetukset.base64Logo && !this.kuvavirheAvain() ? true : false
  }

  private paivitaPiilotaYrityksenNimiLaskullaCheckBox() {
    if (this.asetukset.base64Logo) {
      this._piilotaYrityksenNimiPoissa.set(false)
    } else {
      asetaArvo(this.lomake.piilotaYrityksenNimiLaskulla, false)
      this.asetukset.piilotaYrityksenNimiLaskulla = false
      this._piilotaYrityksenNimiPoissa.set(true)
    }
  }

  ngOnInit() {
    this._osionKentat.set(OSION_KENTAT[this.osio])
    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector })

    // Bind to changes
    // Ohjeet
    muutokset(this.lomake.ohjeet).subscribe(value => {
      this.asetukset.ohjeet = this.validationService.sanitizeWhitespace(value)
    })
    // Pankkiyhteys
    muutokset(this.lomake.pankki).subscribe(value => {
      this.asetukset.pankki = this.validationService.processValue(value)
    })
    muutokset(this.lomake.iban).subscribe(value => {
      this.asetukset.iban = this.stringService.removeAllWhiteSpaces(value) || null
      const pankinNimi = this.bicService.annaPankinNimi(this.asetukset.iban)
      const bic = this.bicService.annaBicKoodi(this.asetukset.iban)
      if (pankinNimi) {
        asetaArvo(this.lomake.pankki, pankinNimi)
      }
      if (bic) {
        asetaArvo(this.lomake.bic, bic)
      }
    })
    muutokset(this.lomake.bic).subscribe(value => {
      this.asetukset.bic = this.validationService.processValue(value)
    })

    muutokset(this.lomake.piilotaYrityksenNimiLaskulla).subscribe(value => {
      this.asetukset.piilotaYrityksenNimiLaskulla = value
    })
    muutokset(this.lomake.oletusMaksuaikaa).subscribe(value => {
      this.asetukset.oletusMaksuaikaa = value
    })
    muutokset(this.lomake.verottomanMyynninOletusperuste).subscribe(value => {
      this.asetukset.verottomanMyynninOletusperuste = value
    })
    muutokset(this.lomake.laskurivienHinnatVerollisina).subscribe(value => {
      this.asetukset.laskurivienHinnatVerollisina = value
    })

    this.alvRekisterissaObservable = this._laskuttavaAsiakasProvider.onkoAlvRekisterissa()
    this.alvRekisterinTilaAvainObservable = combineLatest([this.kayttajaService.nykyinenAsiakasObservable, this.alvRekisterissaObservable]).pipe(
      map(([asiakas, alvRekisterissa]) => {
        const rekisteritietoPuuttuu = !!asiakas && !asiakas.rekisterit
        if (rekisteritietoPuuttuu) {
          return 'laskuasetukset.alv-rekisteri-ei-tiedossa'
        }
        return alvRekisterissa ? 'laskuasetukset.alv-rekisteri-kylla' : 'laskuasetukset.alv-rekisteri-ei'
      }),
    )

    // Load data
    this._laskutusprofiiliService.valittuLaskutusprofiiliObservable.pipe(takeUntil(this.ngUnsubscribe)).subscribe(
      asetukset => {
        // this.form.reset()
        if (asetukset) {
          this.asetukset = this.laskuKopioija.copyAsetukset(asetukset)
          this.asetuksetOletuksetService.poistaOletuksetAsetuksista(this.asetukset)
        } else {
          this.asetukset = {
            avain: null,
            nimi: null,
            ytunnus: null,
            katuosoite: null,
            postitmp: null,
            postinro: null,
            maakoodi: 'FIN',
            date: null,
            replyto: null,
            puhelin: null,
            bic: null,
            iban: null,
            pankki: null,
            base64Logo: null,
            ohjeet: null,
            slogan: null,
            piilotaYrityksenNimiLaskulla: false,
            summahuomautusPiilotettu: false,
            muistutusSpostiasetukset: {},
            spostiasetukset: {},
            tervetuloaNahty: true,
            maksupReskontraActive: true,
          }
        }

        this.asetukset.tervetuloaNahty = true

        // Ohjeet
        asetaArvo(this.lomake.ohjeet, this.asetukset.ohjeet ?? '')

        // Pankkiyhteys
        const trimmed = this.stringService.removeAllWhiteSpaces(this.asetukset.iban || '')
        asetaArvo(this.lomake.iban, this.ibanService.formatoiIban(trimmed) ?? '')
        asetaArvo(this.lomake.pankki, this.asetukset.pankki ?? '')
        asetaArvo(this.lomake.bic, this.asetukset.bic ?? '')

        // Kuuntelija kopioi arvon sellaisenaan, joten ilmoittamatta asetuksiin jää puuttuva arvo eikä valintaruudun false
        asetaArvo(this.lomake.piilotaYrityksenNimiLaskulla, this.asetukset.piilotaYrityksenNimiLaskulla ?? false, { ilmoita: false })
        asetaArvo(this.lomake.oletusMaksuaikaa, this.asetukset.oletusMaksuaikaa ?? OLETUSMAKSUAIKA)
        asetaArvo(this.lomake.verottomanMyynninOletusperuste, this.asetukset.verottomanMyynninOletusperuste ?? null)
        asetaArvo(this.lomake.laskurivienHinnatVerollisina, !!this.asetukset.laskurivienHinnatVerollisina)

        afterNextRender(() => this.nimiInput.focus(), { injector: this._injector })
        this.updateLogoSrc()

        this.kuvanInfotekstinAvain.set('laskuasetukset.logo.info-teksti')

        this.paivitaPiilotaYrityksenNimiLaskullaCheckBox()
        this.lomake().reset()
      },
      err => {
        this.errorHandler.handleError(err)
      },
    )
  }

  onkoTallentamattomiaMuutoksia(): boolean {
    return this.lomake().dirty()
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  async save() {
    if (await this.kayttajaService.kayttajaaImpersonoidaan()) {
      return
    }

    if (this.validationService.onkoLomakkeessaVirheita(this.lomake)) {
      this.validationService.merkitseKokoLomakeKosketuksi(this.lomake)
    } else {
      this.ladataanService.aloitaLataaminen()
      this.laskuService
        .updateLaskuasetukset(this.asetukset, this.asetukset.avain)
        .then(() => {
          const lokalisoitu = this.translationService.lokalisoi('laskuasetukset.tallennettu-onnistuneesti')
          this.snackbar.open(lokalisoitu, 'OK', { duration: 5000, verticalPosition: 'top' })
          this.ladataanService.lopetaLataaminen()
          this.kuvanInfotekstinAvain.set('laskuasetukset.logo.info-teksti')
        })
        .catch(err => {
          this.ladataanService.lopetaLataaminen()
          this.errorHandler.handleError(err)
        })
    }
  }

  private _onkoMuunOsionKentta(kentta: keyof LaskuasetuksetLomake): boolean {
    return !this._osionKentat().includes(kentta)
  }

  private updateLogoSrc(): void {
    if (this.asetukset.base64Logo) {
      this.logo.set('data:;base64,' + this.asetukset.base64Logo)
    } else {
      this.logo.set('/assets/noimage.png')
    }
    this.paivitaPiilotaYrityksenNimiLaskullaCheckBox()
  }

  naytaTiedostonValintaDialogi() {
    this.fileInput.nativeElement.click()
  }

  fileChanged(event) {
    const list: FileList = event.target.files

    const tiedostonjenMaara = list ? list.length : 0
    const fileSuspect = tiedostonjenMaara > 0 ? list.item(0) : null

    if (fileSuspect === null) {
      return
    }

    const fileEnding = this.tiedostojenLataamisService.getFileEndingFromFile(fileSuspect)
    const fileSize = fileSuspect.size ? fileSuspect.size : -1

    if (!this.tiedostoVoidaanLadata(tiedostonjenMaara, fileEnding, fileSize)) {
      return
    }

    return this.optimoiLogo(fileSuspect, './' + fileSuspect.name).catch(error => {
      this.optimoidaanKuvaa.set(false)
      this.kuvavirheAvain.set('laskuasetukset.logo.virheet.tuntematon')
      setTimeout(() => {
        this.kuvavirheAvain.set('')
      }, 10000)
      this.errorHandler.handleError(error)
    })
  }

  private tiedostoVoidaanLadata(kuvienMaara: number, tiedostonPaate: string, tiedostonKoko: number): boolean {
    this.kuvavirheAvain.set('')
    this.kuvavirheParametrit.set({})
    if (kuvienMaara > 1) {
      this.kuvavirheAvain.set('laskuasetukset.logo.virheet.vain-yksi-kerrallaan')
      setTimeout(() => {
        this.kuvavirheAvain.set('')
      }, 10000)
      return false
    }

    if (kuvienMaara < 1) {
      this.kuvavirheAvain.set('laskuasetukset.logo.virheet.ei-tiedosto')
      setTimeout(() => {
        this.kuvavirheAvain.set('')
      }, 10000)
      return false
    }

    const supportedImageTypes = ['jpeg', 'jpg', 'png', 'webp', 'tiff', 'gif', 'svg', 'heic', 'heif']
    if (!tiedostonPaate || supportedImageTypes.indexOf(tiedostonPaate.toLowerCase()) < 0) {
      this.kuvavirheParametrit.set({
        tuetutMuodot: supportedImageTypes.join(', '),
      })
      this.kuvavirheAvain.set('laskuasetukset.logo.virheet.vaara-muoto')
      setTimeout(() => {
        this.kuvavirheAvain.set('')
      }, 10000)
      return false
    }

    const maxKoko = 5 * 1024 * 1024
    if (tiedostonKoko > maxKoko) {
      const kokoMegatavuissaLokalisoitu = this.currencyService.formatoiDesimaali(tiedostonKoko / 1024 / 1024, 2, this.translationService.nykyinenKieli)
      const maxKokoLokalisoitu = this.currencyService.formatoiDesimaali(maxKoko / 1024 / 1024, 2, this.translationService.nykyinenKieli)
      this.kuvavirheParametrit.set({
        kokoMax: maxKokoLokalisoitu,
        kokoNyt: kokoMegatavuissaLokalisoitu,
      })
      this.kuvavirheAvain.set('laskuasetukset.logo.virheet.liian-suuri')
      setTimeout(() => {
        this.kuvavirheAvain.set('')
      }, 10000)
      return false
    }

    return true
  }
}
